import { LayoutDashboard, Inbox, Truck, Banknote, CircleUser } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { OfflineBanner } from './OfflineBanner'
import {
  CURRENT_SPRINT,
  SPRINT_PLAN,
  hideWhenLocked,
  isUnlocked,
  sprintOf,
  stagingActive,
} from '@waago/api'
import { MobileFrame, SprintRibbon, TabBar, type TabItem } from '@waago/ui'
import { ActivityHeader } from './ActivityHeader'
import { CurrentActivityProvider } from '../lib/current-activity'
import { SprintGate } from './SprintGate'
import { ProviderGate } from './ProviderGate'

/**
 * AF bottom navigation, from the Prestataire design.
 *
 * <p>Five destinations, as frame `4650:4369` draws the bar. Paiements was
 * reachable only from inside "Mon compte" until that frame was built, which put
 * a provider's own takings two taps further away than the design puts them.
 */
export function AppTabs() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const items: TabItem[] = (
    [
      {
        to: '/',
        label: t('nav.dashboardShort'),
        icon: <LayoutDashboard className="size-5" aria-hidden="true" />,
      },
      {
        to: '/offres',
        label: t('nav.offers'),
        icon: <Inbox className="size-5" aria-hidden="true" />,
      },
      {
        to: '/missions',
        label: t('nav.missions'),
        icon: <Truck className="size-5" aria-hidden="true" />,
      },
      {
        to: '/revenus',
        label: t('nav.payments'),
        icon: <Banknote className="size-5" aria-hidden="true" />,
      },
      {
        to: '/compte',
        label: t('nav.account'),
        icon: <CircleUser className="size-5" aria-hidden="true" />,
      },
    ] satisfies TabItem[]
  )
    .filter((item) => !hideWhenLocked() || isUnlocked(item.to))
    .map((item) => (isUnlocked(item.to) ? item : { ...item, locked: sprintOf(item.to) }))

  return (
    <CurrentActivityProvider>
      <MobileFrame>
        {stagingActive() ? (
          <SprintRibbon sprint={CURRENT_SPRINT} days={SPRINT_PLAN[CURRENT_SPRINT].days} />
        ) : null}
        {/* The chrome is inside the gate on purpose: an account that is not a
            prestataire yet should not be looking at a tab bar full of screens
            it has nothing behind, nor at a header asking which of its
            activities is current. It gets the one screen that moves it
            forward. */}
        <ProviderGate>
          <ActivityHeader />
          <OfflineBanner />
          <main id="main" className="flex-1 overflow-x-hidden overflow-y-auto pb-2">
            <SprintGate>
              <Outlet />
            </SprintGate>
          </main>
          <TabBar
            items={items}
            activePath={location.pathname}
            onNavigate={(to) => void navigate(to)}
          />
        </ProviderGate>
      </MobileFrame>
    </CurrentActivityProvider>
  )
}
